import { getApi } from '@/service/Api';
import { init } from '@/service/generic.service';
import SocketService, { ListenerChannel } from '@/service/websocket.service';

import { JobSite } from '@/types/JobSite';
import { PaginatedResponse } from '@/types/Pagination';

interface CustomerChanged {
  id: string;
  customerId: string;
}

export interface AlgoliaResponse<T> {
  hits: T[];
  nbHits: number;
}

export interface AlgoliaJobSite {
  objectID: string;
  version: number;
  address: string;
  city: string;
  state: string;
  zip: string;
  street: string;
  updatedAt: number;
  createdAt: number;
  ezCare: string;
  nickname?: string;
}

const genericService = init({ collectionPath: 'job-sites', algoliaIndex: 'job-sites' });

export function search({
  search = '',
  pageSize = 40,
  page = 0,
  searchFilter = undefined,
}): Promise<AlgoliaResponse<AlgoliaJobSite>> {
  return genericService.list({ search, pageSize, page, searchFilter }, false) as unknown as Promise<
    AlgoliaResponse<AlgoliaJobSite>
  >;
}

export async function getByCustomerId(customerId: string): Promise<JobSite[]> {
  const api = getApi();

  const jobSites = await api.get<JobSite[]>('/v3/job-sites', {
    params: { customerId },
  });
  return jobSites.data;
}

export async function getById(id: string): Promise<JobSite> {
  const api = getApi();

  const jobSite = await api.get<JobSite>(`/v3/job-sites/${id}`);
  return jobSite.data;
}

export async function list(jobSite: JobSite): Promise<PaginatedResponse<JobSite>> {
  const api = getApi();

  const jobSites = await api.get<PaginatedResponse<JobSite>>('/v3/job-sites', {
    params: jobSite,
  });
  return jobSites.data;
}

export async function create(jobSite: JobSite): Promise<JobSite> {
  const api = getApi();

  if (!jobSite.street2) {
    jobSite.street2 = '';
  }

  return new Promise((resolve, reject) => {
    let channel: ListenerChannel;
    const timeout = setTimeout(() => {
      channel?.detach();
      reject(new Error('Job site creation timed out after 30 seconds'));
    }, 30000);

    api
      .post<JobSite>('/v3/job-sites', jobSite)
      .then(response => {
        const createdJobSite = response.data;
        channel = SocketService.join('job-site');
        channel.subscribe(`created:${createdJobSite.id}`, () => {
          clearTimeout(timeout);
          channel.detach();
          resolve(createdJobSite);
        });
      })
      .catch(error => {
        clearTimeout(timeout);
        channel?.detach();
        reject(error);
      });
  });
}

export async function remove(id: string): Promise<void> {
  const api = getApi();
  await api.delete(`/v3/job-sites/${id}`);
}

export async function update(id: string, jobSite: JobSite): Promise<JobSite> {
  const api = getApi();
  const response = await api.put<JobSite>(`/v3/job-sites/${id}`, jobSite);
  return response.data;
}

export async function changeCustomer(id: string, customerId: string): Promise<CustomerChanged> {
  const api = getApi();
  const res = await api.post(`/v3/job-sites/${id}/change-customer`, { customerId });
  return res.data;
}

export function joinChannel(): ListenerChannel {
  return SocketService.join('job-site');
}
